import { inject, PLATFORM_ID } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { catchError, forkJoin, Observable, of, tap } from 'rxjs';
import { MetaService } from '../services/meta.service';
import { PageLayoutService } from '../services/page-resolver.service';
import { BlogService } from '../../common/services/blog.service';
import { title } from 'process';
import { RESPONSE } from '../../../express.token';
import { isPlatformServer } from '@angular/common';
import { CommonHelper } from '../../common/helpers/commonHelper';
import { GlobalService } from '../../common/services/global.service';

export const blogPostResolver: ResolveFn<any> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
): Observable<any> => {
    const path = route.url.map((segment) => segment.path);
    const blogService = inject(BlogService);
    const metaService = inject(MetaService);
    const res = inject(RESPONSE, { optional: true });
    const platformId = inject(PLATFORM_ID);
    const router = inject(Router);
    const globalService = inject(GlobalService);

    /**
     * Format month slug
     * @param slug 
     * @returns 
     */
    function formatMonthSlug(slug: string): string {
        if (!slug) return '';

        const [first, second] = slug.split('-');
        const year = first.length === 4 ? first : second;
        const month = first.length === 4 ? second : first;

        const date = new Date(Number(year), Number(month) - 1);
        return `${date.toLocaleString('en-US', { month: 'short' })} ${year}`;
    }

    let ogFallbackImage = globalService.branding?.value?.og_fallback_image || '';
    if (path[1] === '') {
        return of(false);
    }
    if (path[1] === 'category') {
        return blogService.getBlogCategory(path[2]).pipe(
            tap((response) => {
                if (response.body?.data?.redirection_url != null) {
                    router.navigateByUrl(`/${response.body.data.redirection_url}`);
                    return;
                }
                // update meta data
                const title =
                    'Real Estate Blog' +
                    (response.body?.data?.page_data?.name
                        ? ' - ' + response.body?.data?.page_data?.name
                        : '');
                const meta_description = response.body?.data.page_data.meta_description;
                const meta_keywords = response.body?.data.page_data.meta_keywords;
                metaService.updateBlogPageMetaTags({
                    title: title,
                    meta_description: meta_description,
                    meta_keywords: meta_keywords,
                    og_title: title,
                    og_url: path.join('/'),
                    og_image: ogFallbackImage,
                });
            }),
            catchError((error) => {
                if (error.status === 404 ) {
                    if (isPlatformServer(platformId)) {
                        res?.status(404);
                    }
                    globalService.isNotFoundPage.next(false);
                }
                return of(null);
            }),
        );
    }
    if (path[1] === 'author') {
        // update meta data
        const title = 'Real Estate Blog - ' + CommonHelper.slugToCapitalizedString(path[2]);
        const description =
            'Updated every 15 minutes with 100% accurate data - Blog - ' +
            CommonHelper.slugToCapitalizedString(path[2]);
        const keywords =
            'Real estate blogs, Real estate news' +
            ',' +
            CommonHelper.slugToCapitalizedString(path[2]);
        metaService.updateBlogPageMetaTags({
            title: title,
            meta_description: description,
            meta_keywords: keywords,
            og_title: title,
            og_url: path.join('/'),
            og_image: ogFallbackImage,
        });
        return of({ type: 'author', author: path[2] });
    }
    if (path[1] === 'month') {
        const [month, year] = path[2].split('-');
        const formattedMonth = formatMonthSlug(path[2]);
        const title = `Real Estate Blog - ${formattedMonth}`;
        const description =
            'Updated every 15 minutes with 100% accurate data - Blog - ' + month + ',' + year;
        const keywords = 'Real estate blogs, Real estate news' + ',' + month + ',' + year;
        metaService.updateBlogPageMetaTags({
            title: title,
            meta_description: description,
            meta_keywords: keywords,
            og_title: title,
            og_url: path.join('/'),
            og_image: ogFallbackImage,
        });
        return of({ type: 'month', date: path[2] });
    }

    // Admin "preview" links carry ?preview_token= so web-api serves an inactive / scheduled post
    const previewToken = route.queryParamMap.get('preview_token');
    return blogService.getOneBlog(path[1], previewToken ? { preview_token: previewToken } : {}).pipe(
        tap((response) => {
            if (response.body?.data?.redirection != null || response.body?.data?.legacy_redirection != null) {
                if (isPlatformServer(platformId)) {
                    res?.redirect(
                        response.body?.data?.redirection?.redirection_type || 301,
                        `/${response.body?.data?.redirection?.redirect_url || response.body?.data?.legacy_redirection?.redirect_url || ''}`,
                    );
                } else {
                    router.navigateByUrl(`/${response.body.data.redirection_url || response.body?.data?.legacy_redirection_url}`);
                }
                return;
            }
            // Show the preview strip (reset on every NavigationStart)
            globalService.blogPreview.next(!!previewToken);
            // update meta data
            metaService.updateBlogPageMetaTags({
                title: response.body?.data.page_data.title,
                meta_description: response.body?.data.page_data.meta_description,
                meta_keywords: response.body?.data.page_data.meta_keywords,
                og_title: response.body?.data.page_data.title,
                og_url: path.join('/'),
                og_image: response.body?.data.page_data.photo_url
                    ? (response.body?.data.page_data.photo_url)
                    : ogFallbackImage,
            });
        }),
        catchError((error) => {
            if (error.status === 404 ) {
                if (isPlatformServer(platformId)) {
                    res?.status(404);
                }
                globalService.isNotFoundPage.next(false);
            }
            return of(null);
        }),
    );
};
